import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { NgbDate, NgbDatepicker } from '@ng-bootstrap/ng-bootstrap';
import moment from 'moment';
import { Subscription } from 'rxjs';
import { debounceTime, filter } from 'rxjs/operators';
import { timeSpanValidator } from 'src/app/shared/extensions/date-time.extensions';
import { TranslationService } from 'src/app/shared/transloco/services/translation.service';
import { IDateTimeRange } from './date-time-range.interface';

@Component({
    selector: 'app-standard-range-date-picker',
    templateUrl: './standard-range-date-picker.component.html',
    styleUrls: ['./standard-range-date-picker.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class StandardRangeDatePickerComponent implements OnInit, OnDestroy {
    @Input() public startTime = moment().format();
    @Input() public endTime = moment().format();

    @Output() public dateTimeRangeChangedEvent = new EventEmitter<IDateTimeRange>();

    @ViewChild('datepicker', { static: true }) private datepicker: NgbDatepicker;

    public subscription = new Subscription();
    public label: string;
    public hoveredDate?: NgbDate = null;
    public fromDate?: NgbDate;
    public toDate?: NgbDate = null;
    public form: UntypedFormGroup;

    private readonly fullDateTimeFormat = 'yyyy-MM-DD HH:mm:ss';
    private readonly dateTimeFormat = 'yyyy-MM-DD HH:mm';
    private translationService = inject(TranslationService);
    private cdRef = inject(ChangeDetectorRef);

    public ngOnInit(): void {
        this.initializeForm();

        this.setTogglerLabel();

        this.onStartTimeChanges();

        this.onEndTimeChanges();
    }

    public ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    public onDateSelection(date: NgbDate) {
        const startTime = moment(this.startTime).set({ year: date.year, months: date.month - 1, date: date.day });
        const endTime = moment(this.endTime).set({ year: date.year, months: date.month - 1, date: date.day });

        if (!this.fromDate && !this.toDate) {
            this.fromDate = date;

            this.form.controls.startTime.setValue(startTime.format(this.fullDateTimeFormat));
        } else if (this.fromDate && !this.toDate && date.after(this.fromDate)) {
            this.toDate = date;

            this.form.controls.endTime.setValue(endTime.format(this.fullDateTimeFormat));
        } else {
            this.toDate = null;
            this.fromDate = date;

            this.form.controls.startTime.setValue(startTime.format(this.fullDateTimeFormat));

            startTime.set({ hours: 23, minutes: 59, seconds: 59 });
            this.form.controls.endTime.setValue(startTime.format(this.fullDateTimeFormat), { emitEvent: false });
        }

        this.form.markAsDirty();
    }

    public isHovered(date: NgbDate) {
        return this.fromDate && !this.toDate && this.hoveredDate && date.after(this.fromDate) && date.before(this.hoveredDate);
    }

    public isInsideTimeSpan(date: NgbDate) {
        return this.toDate && date.after(this.fromDate) && date.before(this.toDate);
    }

    public isRange(date: NgbDate) {
        return date.equals(this.fromDate) || (this.toDate && date.equals(this.toDate)) || this.isInsideTimeSpan(date) || this.isHovered(date);
    }

    public onTimeRangeOpening(isOpening: boolean) {
        if (!isOpening) {
            return;
        }

        this.setDates();
    }

    public save() {
        if (this.form.invalid) {
            return;
        }

        const selectedDateTimeRange = this.form.value as IDateTimeRange;
        this.setTogglerLabel();

        this.dateTimeRangeChangedEvent.emit(selectedDateTimeRange);
    }

    private initializeForm() {
        this.form = new UntypedFormGroup(
            {
                startTime: new UntypedFormControl(null),
                endTime: new UntypedFormControl(null),
            },
            { asyncValidators: timeSpanValidator(this.translationService) }
        );

        this.setDates();
    }

    private setDates() {
        const startTime = moment(this.startTime);
        const endTime = moment(this.endTime);

        this.form.patchValue({ startTime: startTime.format(this.fullDateTimeFormat), endTime: endTime.format(this.fullDateTimeFormat) });
    }

    private setTogglerLabel() {
        const startTime = this.form.controls['startTime'].value;
        const endTime = this.form.controls['endTime'].value;

        this.label = `${moment(startTime).format(this.dateTimeFormat)} - ${moment(endTime).format(this.dateTimeFormat)}`;
    }

    private onStartTimeChanges() {
        this.subscription.add(
            this.form.controls['startTime'].valueChanges
                .pipe(debounceTime(300))
                .pipe(filter(() => this.form.controls['startTime'].valid))
                .subscribe((value) => {
                    const startTime = moment(value);
                    const fromDate = new NgbDate(Number(startTime.format('YYYY')), Number(startTime.format('M')), Number(startTime.format('D')));
                    this.fromDate = fromDate;

                    this.datepicker.navigateTo(fromDate);

                    this.cdRef.detectChanges();
                })
        );
    }

    private onEndTimeChanges() {
        this.subscription.add(
            this.form.controls['endTime'].valueChanges
                .pipe(debounceTime(300))
                .pipe(filter(() => this.form.controls['endTime'].valid))
                .subscribe((value) => {
                    const endTime = moment(value);
                    const toDate = new NgbDate(Number(endTime.format('YYYY')), Number(endTime.format('M')), Number(endTime.format('D')));
                    this.toDate = toDate;

                    this.cdRef.detectChanges();
                })
        );
    }
}
